import type { ParsedSheets } from './xlsxParser';

export type ValidationWarning = {
  sheet: string;
  severity: 'warning' | 'error';
  message: string;
  count?: number;
};

/**
 * Performs client-side preflight validation on parsed sheets.
 * Checks for common issues like missing required fields, empty values, etc.
 */
export function validateSheets(sheets: ParsedSheets): ValidationWarning[] {
  const warnings: ValidationWarning[] = [];

  // Required field definitions
  const requiredFields: Record<string, string[]> = {
    location_types: ['key', 'label'],
    organization_types: ['key', 'label'],
    topic_types: ['key', 'label'],
    service_types: ['key', 'label'],
    locations: ['name'],
    organizations: ['name', 'slug', 'organizationTypeKey'],
    topics: ['name', 'slug', 'topicTypeKey'],
    sections: ['section_key', 'label_en'],
    service_definitions: ['name', 'slug', 'serviceTypeKey'],
    users: ['email'],
  };

  for (const [sheetName, rows] of Object.entries(sheets)) {
    const required = requiredFields[sheetName as keyof typeof requiredFields] || [];

    // Check for empty required fields
    for (const field of required) {
      const emptyCount = rows.filter((row) => !row[field]?.trim()).length;
      if (emptyCount > 0) {
        warnings.push({
          sheet: sheetName,
          severity: 'error',
          message: `${emptyCount} ${emptyCount === 1 ? 'row has' : 'rows have'} missing required field: ${field}`,
          count: emptyCount,
        });
      }
    }

    // Check for duplicate keys/slugs/emails
    const uniqueFields: Record<string, string> = {
      location_types: 'key',
      organization_types: 'key',
      topic_types: 'key',
      service_types: 'key',
      locations: 'slug',
      organizations: 'slug',
      topics: 'slug',
      sections: 'section_key',
      service_definitions: 'slug',
      users: 'email',
    };

    const uniqueField = uniqueFields[sheetName as keyof typeof uniqueFields];
    if (uniqueField) {
      const values = rows.map((r) => r[uniqueField]?.trim().toLowerCase()).filter(Boolean);
      const duplicates = values.filter((v, i, arr) => arr.indexOf(v) !== i);
      const uniqueDuplicates = Array.from(new Set(duplicates));
      if (uniqueDuplicates.length > 0) {
        warnings.push({
          sheet: sheetName,
          severity: 'error',
          message: `Duplicate ${uniqueField} found: ${uniqueDuplicates.slice(0, 3).join(', ')}${uniqueDuplicates.length > 3 ? ` (+${uniqueDuplicates.length - 3} more)` : ''}`,
          count: uniqueDuplicates.length,
        });
      }
    }

    // Warning: parentless hierarchical entities are good, but ALL having parents is suspicious
    const hierarchicalFields: Record<string, string> = {
      locations: 'parentLocationSlug',
      organizations: 'parentOrganizationSlug',
      topics: 'parentTopicSlug',
      sections: 'parent_section_key',
    };

    const parentField = hierarchicalFields[sheetName as keyof typeof hierarchicalFields];
    if (parentField) {
      const allHaveParents = rows.length > 0 && rows.every((r) => r[parentField]?.trim());
      if (allHaveParents) {
        warnings.push({
          sheet: sheetName,
          severity: 'warning',
          message: 'All rows have a parent reference — ensure at least one root exists or is already in the database',
        });
      }
    }

    // Warning: check for common field name typos
    const columnNames = Object.keys(rows[0] || {});
    const commonTypos: Record<string, string> = {
      'locationtype': 'locationTypeKey',
      'organizationtype': 'organizationTypeKey',
      'topictype': 'topicTypeKey',
      'servicetype': 'serviceTypeKey',
      'parent': 'parentLocationSlug or parentOrganizationSlug',
    };
    for (const [typo, correct] of Object.entries(commonTypos)) {
      if (columnNames.some((col) => col.toLowerCase() === typo)) {
        warnings.push({
          sheet: sheetName,
          severity: 'warning',
          message: `Column '${typo}' may be a typo — did you mean '${correct}'?`,
        });
      }
    }
  }

  return warnings;
}



